// ** React Imports
import React, { useCallback, useState, useEffect } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

// ** Styles Imports
import Styles from './styles.module.css'

// ** Redux Imports
import { useSelector, useDispatch } from 'react-redux'
import { _withdraw } from '../../../../security/Enable2FAModal/redux/actions'

// ** Form Validation Imports
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// ** Component Imports
import Input from '../../../../../../components/Input/Input'
import Button from '../../../../../../components/Button/Button'
import CoinDisplay from '../../../../../../components/CoinDisplay/CoinDisplay'
import ShimmerLine from '../../../../../../components/shimmer-components/ShimmerLine'
import SelectBeneficiaryInput from '../../../../../../components/Input/SelectBeneficiaryInput/SelectBeneficiaryInput'

// ** Third Party Imports
import { FormattedMessage } from 'react-intl'

// ** Utils Imports
import { debounce } from '@mui/material'
import { showErrorSnackbar, showSuccessSnackbar } from '../../../../../snackbar/helpers'
import { showCreateBeneficiaryModal } from '../../../../beneficiaries/create/helpers'
import {
  hideVerificationCodesInputModal,
  showVerificationCodesInputModal
} from '../../../../auth/VerificationCodesInput/helpers'
import { closeWithdrawFormModal } from '../../helpers'
import { setBalances } from '../../../../../../redux/slices/balancesSlice'
import { _getBalances } from '../../../../../exchange/redux/actions'
import { formatNumber } from '../../../../../helpers/formatNumber'
import { _getWithdrawFee, _requestWithdraw } from '../../../redux/actions'
import { _resendEmailOTP, _resendPhoneOTP } from '../../../../auth/VerificationCodesInput/redux/actions'

function CryptoNetworkTab() {
  // ** Form Schema
  const withdrawSchema = Yup.object().shape({
    amount: Yup.number('Amount must be a number').typeError('Amount must be a number').required('Amount is required'),
    beneficiary_id: Yup.string().required('A beneficiary is required'),
    note: Yup.string().max(256)
  })

  // ** Hooks
  const dispatch = useDispatch()
  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors },
    reset,
    setValue,
    setError
  } = useForm({
    resolver: yupResolver(withdrawSchema),
    mode: 'onSubmit'
  })

  // ** States
  const theme = useSelector(state => state.theme.value)
  const balances = useSelector(state => state.balances.value)
  const currencies = useSelector(state => state.currencies.value)
  const modalData = useSelector(state => state.withdrawFormModalState.data)
  const [amount, setAmount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [transactionFee, setTransactionFee] = useState(0)
  const [userReceived, setUserReceived] = useState(0)
  const [transactionFeeLoading, setTransactionFeeLoading] = useState(false)
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null)
  const [recaptchaConfirmed, setRecaptchaConfirmed] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [withdrawData, setWithdrawData] = useState(null)
  const [resendTimers, setResendTimers] = useState({
    email: 0,
    sms: 0
  })

  useEffect(() => {
    _getBalances(
      res => {
        dispatch(setBalances(res))
      },
      () => { }
    )
  }, [])

  // ** Handle max amount button click
  const handleMaxAmount = () => {
    getTransactionFee(balances)
    setAmount(balances.find(el => el.currency === modalData?.selectedCoinID)?.balance ?? 0)
    setValue('amount', balances.find(el => el.currency === modalData?.selectedCoinID)?.balance ?? 0)
    trigger('amount')
  }

  const recaptchaOnChange = value => {
    setRecaptchaConfirmed(true)
    setRecaptchaResponse(value)
  }

  const resendEmailCode = () => {
    // Don't resend if timer is active
    if (resendTimers.email > 0) return Promise.reject('Email code resend on cooldown')

    // Send as a promise to be able to set the timer on successful response
    return new Promise((resolve, reject) => {
      _resendEmailOTP(
        {
          withdraw_id: withdrawData?.id // Add withdraw_id if required by your API
        },
        data => {
          showSuccessSnackbar({ alertMessage: 'Email code sent' })

          // Set timer for email
          setResendTimers(prev => ({ ...prev, email: 60 }))

          // Start countdown
          const interval = setInterval(() => {
            setResendTimers(prev => {
              const newEmailTimer = prev.email - 1
              if (newEmailTimer <= 0) {
                clearInterval(interval)
              }
              return { ...prev, email: Math.max(0, newEmailTimer) }
            })
          }, 1000)

          resolve(data)
        },
        err => {
          showErrorSnackbar({
            alertMessage: err.errors && err.errors[0] ? err.errors[0] : 'Failed to send email code'
          })
          reject(err)
        }
      )
    })
  }

  const resendSMSCode = () => {
    // Don't resend if timer is active
    if (resendTimers.sms > 0) return Promise.reject('SMS code resend on cooldown')

    // Send as a promise to be able to set the timer on successful response
    return new Promise((resolve, reject) => {
      _resendPhoneOTP(
        {
          withdraw_id: withdrawData?.id // Add withdraw_id if required by your API
        },
        data => {
          showSuccessSnackbar({ alertMessage: 'SMS code sent' })

          // Set timer for SMS
          setResendTimers(prev => ({ ...prev, sms: 60 }))

          // Start countdown
          const interval = setInterval(() => {
            setResendTimers(prev => {
              const newSmsTimer = prev.sms - 1
              if (newSmsTimer <= 0) {
                clearInterval(interval)
              }
              return { ...prev, sms: Math.max(0, newSmsTimer) }
            })
          }, 1000)

          resolve(data)
        },
        err => {
          showErrorSnackbar({
            alertMessage: err.errors && err.errors[0] ? err.errors[0] : 'Failed to send SMS code'
          })
          reject(err)
        }
      )
    })
  }

  const handleCodeInputSubmit = formData => {
    // if (!withdrawData) {
    //   showErrorSnackbar({ alertMessage: "Withdraw data not available" })
    //   return
    // }

    _withdraw(
      {
        phone_otp: formData?.smsCode,
        email_otp: formData?.emailCode,
        otp: formData?.otpCode,
        // withdraw_id: withdrawData.id, // Pass withdraw_id for verification
        captcha_response: recaptchaResponse
      },
      data => {
        showSuccessSnackbar({
          alertMessage: 'Withdraw completed'
        })
        hideVerificationCodesInputModal()
        closeWithdrawFormModal()
      },
      err => {
        showErrorSnackbar({
          alertMessage: err.errors && err.errors[0] ? err.errors[0] : 'Withdraw was not successful'
        })
      }
    )
  }

  // ** Submit function
  const onSumbit = formData => {
    setLoading(true)
    _requestWithdraw(
      {
        currency: modalData?.selectedCoinID,
        amount: formData.amount,
        beneficiary_id: formData.beneficiary_id,
        note: formData.note,
        captcha_response: recaptchaResponse
      },
      data => {
        setLoading(false)
        setWithdrawData(data) // Store withdraw data for code verification

        showVerificationCodesInputModal({
          title: 'Withdraw Verification',
          sms: true,
          otp: true,
          email: true,
          initialEmailTimer: 60,
          initialSMSTimer: 60,
          handleEmailResendCode: resendEmailCode,
          handleSMSResendCode: resendSMSCode,
          callback: handleCodeInputSubmit
        })
      },
      err => {
        showErrorSnackbar({
          alertMessage:
            err.errors && err.errors[0] && err.errors[0].length ? (
              <FormattedMessage id={err.errors[0]} />
            ) : (
              'Withdraw was not successful'
            )
        })
        setLoading(false)
      }
    )
  }

  // ** On amount change -> get new fee
  const getTransactionFee = useCallback(
    debounce(balances => {
      // If balance < amount input --> return
      if (
        Number(balances.find(el => el.currency === modalData?.selectedCoinID)?.balance ?? 0) <
        Number(getValues('amount'))
      ) {
        // Display error and return
        setError('amount', { message: 'Insufficient balance' })
        setTransactionFee(0)
        return
      }
      setTransactionFeeLoading(true)
      _getWithdrawFee(
        {
          amount: getValues('amount'),
          currency: modalData?.selectedCoinID
        },
        data => {
          setTransactionFeeLoading(false)
          setTransactionFee(data?.fees)
          setUserReceived(data?.user_received)
        },
        err => {
          setTransactionFeeLoading(false)
        }
      )
    }, 2000),
    []
  )

  return (
    <form
      style={{
        paddingTop: 20
      }}
      onSubmit={handleSubmit(onSumbit)}
    >
      <span className={Styles.label}>From</span>
      <div className={`${Styles.infoContainer} ${Styles.fromCoinContainer}`}>
        <div className={Styles.fromCoinLeftContainer}>
          <CoinDisplay
            coinImgURL={currencies.find(el => el?.id === modalData?.selectedCoinID)?.icon_url}
            coinName={currencies.find(el => el?.id === modalData?.selectedCoinID)?.name}
            coinTag={currencies.find(el => el?.id === modalData?.selectedCoinID)?.id?.toUpperCase()}
          />
        </div>
        <span>
          Available{' '}
          {balances.find(el => el.currency === modalData?.selectedCoinID)?.balance > 0
            ? formatNumber(balances.find(el => el.currency === modalData?.selectedCoinID)?.balance, 6)
            : 0.0}
        </span>
      </div>

      <SelectBeneficiaryInput
        coinId={modalData?.selectedCoinID}
        onChange={beneficiary => {
          setSelectedBeneficiary(beneficiary)
          setValue('beneficiary_id', beneficiary?.id)
          trigger('beneficiary_id')
        }}
        beneficiaryName={selectedBeneficiary?.name}
        error={errors.beneficiary_id}
      />

      {/* Beneficiary Information Display */}
      {selectedBeneficiary && (
        <div className={Styles.beneficiaryInfoContainer}>
          <h4 className={Styles.beneficiaryInfoTitle}>Beneficiary Information</h4>
          <ul className={Styles.beneficiaryInfoList}>
            <li className={Styles.beneficiaryInfoItem}>
              <span className={Styles.infoLabel}>Name:</span>
              <span className={Styles.infoValue}>{selectedBeneficiary.name}</span>
            </li>
            <li className={Styles.beneficiaryInfoItem}>
              <span className={Styles.infoLabel}>Network:</span>
              <span className={Styles.infoValue}>{selectedBeneficiary.network}</span>
            </li>
            <li className={Styles.beneficiaryInfoItem}>
              <span className={Styles.infoLabel}>Address:</span>
              <span className={Styles.infoValue}>{selectedBeneficiary.data?.address}</span>
            </li>
            {selectedBeneficiary.description && (
              <li className={Styles.beneficiaryInfoItem}>
                <span className={Styles.infoLabel}>Description:</span>
                <span className={Styles.infoValue}>{selectedBeneficiary.description}</span>
              </li>
            )}
            <li className={Styles.beneficiaryInfoItem}>
              <span className={Styles.infoLabel}>Status:</span>
              <span className={`${Styles.infoValue} ${Styles.statusValue}`}>{selectedBeneficiary.state}</span>
            </li>
          </ul>
          <div className={Styles.warningMsg}>Please verify all details before proceeding with the withdrawal.</div>
        </div>
      )}

      <Button
        text='Add beneficiary'
        variant='link'
        className={Styles.addBeneficiaryButton}
        onClick={() => {
          showCreateBeneficiaryModal({
            selectedCoin: modalData?.selectedCoinID
          })
        }}
      />

      {/* AMOUNT INPUT */}
      <Input
        inputLabel='Amount'
        inputIconContainerStyle={{
          border: '1px var(--border-color) solid',
          borderRadius: 5,
          height: 45,
          background: 'var(--secondary-color)'
        }}
        style={{ fontSize: 13, background: 'transparent' }}
        value={amount || ''}
        placeholder='Enter amount'
        error={errors.amount}
        onChange={e => {
          getTransactionFee(balances)
          setAmount(e?.target?.value)
          setValue('amount', e?.target?.value)
          trigger('amount')
        }}
        icon={
          <Button
            text='MAX'
            variant='link'
            onClick={handleMaxAmount}
            style={{
              padding: 0,
              width: 'fit-content'
            }}
          />
        }
      />

      {/* Transaction Fee Display */}
      {transactionFee > 0 && (
        <div className={Styles.feeContainer}>
          <span className={Styles.feeLabel}>Transaction Fee:</span>
          <span className={Styles.feeValue}>
            {formatNumber(transactionFee, 6)} {modalData?.selectedCoinID?.toUpperCase()}
          </span>
        </div>
      )}
      {userReceived > 0 && (
        <div className={Styles.feeContainer}>
          <span className={Styles.feeLabel}>You are withdrawing:</span>
          <span className={Styles.feeValue}>
            {formatNumber(userReceived, 6)} {modalData?.selectedCoinID?.toUpperCase()}
          </span>
        </div>
      )}
      {userReceived > 0 && (
        <div className={Styles.feeContainer} style={{ fontSize: '12px', color: 'var(--gray-color)', fontStyle: 'italic' }}>
          Please note that withdrawal fees and bank processing fees will apply. The total amount received may be less than the amount requested due to applicable charges deducted by your bank or intermediary financial institutions.
        </div>
      )}
      {transactionFeeLoading && (
        <div className={Styles.feeContainer}>
          <span className={Styles.feeLabel}>Calculating fees...</span>
          <ShimmerLine width='80px' height='18px' />
        </div>
      )}

      <Input
        type={'textarea'}
        inputLabel='Note (optional)'
        inputIconContainerStyle={{
          border: '1px var(--border-color) solid',
          borderRadius: 5,
          background: 'var(--secondary-color)'
        }}
        style={{ fontSize: 13, background: 'transparent' }}
        placeholder='Add a note (max 256 characters)'
        error={errors.note}
        {...register('note')}
      />

      <ReCAPTCHA
        theme={theme}
        key={theme}
        sitekey={process.env.REACT_APP_SITE_KEY}
        onChange={recaptchaOnChange}
        style={{ margin: '0 auto 20px auto', width: 'fit-content' }}
      />

      <div
        className={Styles.validAddress}
        style={{
          transform: 'translateY(-16px)',
          color: 'var(--gray-color)'
        }}
      >
        Transaction Fees Will Apply
      </div>

      {/* Withdrawal Summary */}
      {selectedBeneficiary && amount > 0 && (
        <div className={Styles.withdrawalSummary}>
          <h4 className={Styles.summaryTitle}>Withdrawal Summary</h4>
          <div className={Styles.summaryItem}>
            <span>You are withdrawing</span>
            <span className={Styles.summaryHighlight}>
              {formatNumber(amount, 6)} {modalData?.selectedCoinID?.toUpperCase()}
            </span>
          </div>
          <div className={Styles.summaryItem}>
            <span>To</span>
            <span className={Styles.summaryHighlight}>{selectedBeneficiary.name}</span>
          </div>
          <div className={Styles.summaryItem}>
            <span>Network</span>
            <span className={Styles.summaryHighlight}>{selectedBeneficiary.network}</span>
          </div>
          {transactionFee > 0 && (
            <div className={Styles.summaryItem}>
              <span>Fee</span>
              <span className={Styles.summaryHighlight}>
                {formatNumber(transactionFee, 6)} {modalData?.selectedCoinID?.toUpperCase()}
              </span>
            </div>
          )}
          <div className={Styles.summaryItem}>
            <span>You are withdrawing</span>
            <span className={Styles.summaryReceive}>
              {formatNumber(userReceived, 6)} {modalData?.selectedCoinID?.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* PROCEED BUTTON */}
      <Button
        text='Proceed withdrawal'
        type='submit'
        disabled={!recaptchaConfirmed || !selectedBeneficiary}
        style={{ marginBottom: 20, color: 'white' }}
        isLoading={loading}
      />
    </form>
  )
}

export default CryptoNetworkTab
